import React from 'react';
import { Box, Typography, Button, Container, Grid } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import FilterListIcon from '@mui/icons-material/FilterList';
import SavedSearchIcon from '@mui/icons-material/SavedSearch';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getConfig } from '../../common/env-config';

const RecruitInfluencers = () => {
    const { ecWebsiteUrl } = getConfig();
    return (
        <Box className="bg-white min-h-screen font-sans text-gray-900 pt-20">
            {/* 1. Hero Section */}
            <Container maxWidth="lg" sx={{ pt: 6, pb: 10 }}>
                <Grid container spacing={6} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <div className="inline-block px-4 py-1.5 rounded-full bg-indigo-50 text-indigo-600 font-bold text-xs uppercase tracking-wide mb-6 animate-fade-in-up">
                            Recruitment Tools
                        </div>
                        <Typography variant="h1" className="text-4xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-6 animate-fade-in-up delay-100">
                            Find the Perfect <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-violet-600">Voices for Your Brand</span>
                        </Typography>
                        <Typography variant="body1" className="text-lg text-gray-600 mb-8 leading-relaxed animate-fade-in-up delay-200">
                            Stop guessing. Use advanced search tactics, lookalike modeling, and niche filtering to recruit influencers who truly resonate with your audience.
                        </Typography>
                        <div className="flex flex-wrap gap-4 animate-fade-in-up delay-300">
                            <Button
                                variant="contained"
                                size="large"
                                href={ecWebsiteUrl + "signup"}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    background: 'linear-gradient(to right, #4f46e5, #7c3aed)',
                                    boxShadow: '0 10px 25px -5px rgba(79, 70, 229, 0.4)',
                                    px: 4, py: 1.5,
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'scale(1.05)', boxShadow: '0 15px 30px -5px rgba(79, 70, 229, 0.5)' }
                                }}
                            >
                                Start Recruiting
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                href="https://outlook.office365.com/book/ProductDemo@ttlmedia.in/?ismsaljsauthenabled=true"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    borderColor: '#e5e7eb',
                                    color: '#374151',
                                    px: 4, py: 1.5,
                                    transition: 'all 0.2s',
                                    '&:hover': { borderColor: '#9ca3af', backgroundColor: '#f9fafb', transform: 'scale(1.05)' }
                                }}
                            >
                                Get a Demo
                            </Button>
                        </div>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {/* Abstract Visual */}
                        <Box className="relative w-full aspect-square md:aspect-[4/3] animate-float">
                            <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-violet-50 rounded-3xl border border-white shadow-2xl overflow-hidden flex items-center justify-center">
                                {/* Background Elements */}
                                <div className="absolute inset-0 opacity-60">
                                    <div className="absolute top-[30%] left-[20%] w-40 h-40 bg-indigo-400/20 rounded-full blur-3xl animate-pulse-slow"></div>
                                    <div className="absolute bottom-[10%] right-[30%] w-32 h-32 bg-violet-400/20 rounded-full blur-3xl animate-pulse-slow delay-1000"></div>
                                </div>

                                {/* Center UI Element: Search Card */}
                                <div className="relative z-10 w-2/3 bg-white/60 backdrop-blur-md border border-white/80 p-6 rounded-2xl shadow-xl">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                                            <PersonSearchIcon className="text-indigo-600" />
                                        </div>
                                        <div className="flex-1 h-3 bg-gray-200 rounded-full w-full"></div>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="h-2 bg-gray-200 rounded-full w-3/4"></div>
                                        <div className="h-2 bg-gray-200 rounded-full w-1/2"></div>
                                        <div className="h-2 bg-gray-200 rounded-full w-5/6"></div>
                                    </div>
                                    {/* Validated Badge */}
                                    <div className="absolute -top-4 -right-4 bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold shadow-sm flex items-center gap-1">
                                        <CheckCircleIcon fontSize="small" /> Match
                                    </div>
                                </div>

                                {/* Floating Elements */}
                                <div className="absolute top-[15%] left-[10%] bg-white p-3 rounded-xl shadow-lg animate-bounce-custom delay-100">
                                    <FilterListIcon className="text-pink-500" />
                                </div>
                                <div className="absolute bottom-[20%] right-[10%] bg-white p-3 rounded-xl shadow-lg animate-bounce-custom delay-300">
                                    <SavedSearchIcon className="text-blue-500" />
                                </div>
                            </div>
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            {/* 2. Features Grid */}
            <Container maxWidth="lg" sx={{ py: 16 }}>
                <div className="text-center mb-12">
                    <Typography variant="h2" className="text-3xl lg:text-4xl font-bold text-gray-900">
                        Smart Recruitment Features
                    </Typography>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Feature 1: Advanced Search (Large) */}
                    <div className="md:col-span-2 bg-indigo-900 rounded-3xl p-8 lg:p-10 text-white relative overflow-hidden group hover:shadow-2xl hover:shadow-indigo-900/20 transition-all duration-500 cursor-pointer">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500 rounded-full mix-blend-overlay filter blur-3xl opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
                        <div className="relative z-10">
                            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <ManageSearchIcon className="text-white" />
                            </div>
                            <h3 className="text-2xl font-bold mb-4">Advanced Filters & Search</h3>
                            <p className="text-indigo-200 mb-6 max-w-md group-hover:text-white transition-colors">
                                Filter by 35+ data points including engagement rate, audience demographics, growth trends, and brand affinities.
                            </p>
                        </div>
                    </div>

                    {/* Feature 2: Niche Discovery */}
                    <div className="bg-white border border-gray-200 rounded-3xl p-8 hover:border-indigo-400 hover:shadow-lg transition-all duration-300 group cursor-pointer">
                        <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center mb-6 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                            <FilterListIcon />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Niche Targeting</h3>
                        <p className="text-gray-500 text-sm">Find micro-influencers in specific niches like sustainable fashion, keto diet, or mobile gaming.</p>
                    </div>

                    {/* Feature 3: Lookalikes */}
                    <div className="bg-white border border-gray-200 rounded-3xl p-8 hover:border-violet-400 hover:shadow-lg transition-all duration-300 group cursor-pointer">
                        <div className="w-12 h-12 bg-violet-50 rounded-xl flex items-center justify-center mb-6 text-violet-600 group-hover:bg-violet-600 group-hover:text-white transition-colors">
                            <GroupAddIcon />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Lookalike Models</h3>
                        <p className="text-gray-500 text-sm">Upload your best paying customers or top performing creators to find others just like them.</p>
                    </div>

                    {/* Feature 4: Inbound Recruitment (Wide) */}
                    <div className="md:col-span-2 bg-gray-50 rounded-3xl p-8 lg:p-10 border border-gray-100 hover:border-gray-300 transition-all flex flex-col md:flex-row items-center gap-8 group">
                        <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Inbound Application Forms</h3>
                            <p className="text-gray-600 text-sm mb-4">Create branded landing pages to accept creator applications. Auto-qualify them based on your criteria.</p>
                            <ul className="space-y-2 text-sm text-gray-500">
                                <li className="flex items-center gap-2"><CheckCircleIcon fontSize="small" className="text-green-500" /> Custom branding</li>
                                <li className="flex items-center gap-2"><CheckCircleIcon fontSize="small" className="text-green-500" /> Auto-rejection rules</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </Container>

            {/* CTA */}
            <Container maxWidth="md" sx={{ py: 16, textAlign: 'center' }}>
                <Typography variant="h2" className="text-4xl lg:text-5xl font-extrabold text-gray-900 mb-6">
                    Ready to Build Your Team?
                </Typography>
                <div className="flex justify-center gap-4">
                    <Button
                        variant="contained"
                        size="large"
                        href={ecWebsiteUrl + "signup"}
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                            borderRadius: '50px',
                            px: 6, py: 2,
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            background: '#E91E63',
                            boxShadow: '0 20px 40px -10px rgba(233, 30, 99, 0.4)',
                            transition: 'transform 0.2s',
                            '&:hover': { background: '#D81B60', transform: 'scale(1.05)' }
                        }}
                    >
                        Start Recruiting Free
                    </Button>
                </div>
            </Container>
        </Box>
    );
};

export default RecruitInfluencers;
